import { useState } from 'react';
import Button from '@/components/base/Button';
import Modal from '@/components/base/Modal';
import type { SavedRecipientList } from '@/types/portal';

interface SavedListsBarProps {
  lists: SavedRecipientList[];
  onLoad: (list: SavedRecipientList) => void;
  onSave: (name: string) => void;
}

export default function SavedListsBar({ lists, onLoad, onSave }: SavedListsBarProps) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');

  const confirm = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    onSave(trimmed);
    setName('');
    setOpen(false);
  };

  return (
    <div className="rounded-lg border border-background-200 bg-background-100/70 px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 text-xs font-label font-medium text-foreground-600">
            <i className="ri-bookmark-3-line" />
            Saved lists
          </span>
          {lists.length === 0 ? (
            <span className="text-xs text-foreground-400">None saved yet</span>
          ) : (
            lists.map((list) => (
              <button
                key={list.id}
                type="button"
                onClick={() => onLoad(list)}
                className="inline-flex items-center gap-1.5 rounded-full border border-background-300 bg-background-50 px-2.5 py-1 text-xs font-label font-medium text-foreground-700 cursor-pointer transition-colors hover:border-primary-300 hover:text-primary-800"
              >
                {list.name}
                <span className="rounded-full bg-background-200 px-1.5 text-[10px] text-foreground-600">
                  {list.recipients.length}
                </span>
              </button>
            ))
          )}
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1.5 text-xs font-label font-medium text-primary-700 cursor-pointer transition-colors hover:text-primary-900"
        >
          <i className="ri-add-line" />
          Save current list
        </button>
      </div>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        icon="ri-bookmark-3-line"
        title="Save recipient list"
        subtitle="Reuse this roster on a future bulk transfer."
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button icon="ri-check-line" onClick={confirm} disabled={!name.trim()}>
              Save list
            </Button>
          </>
        }
      >
        <label htmlFor="list-name" className="text-xs font-label font-medium text-foreground-600">
          List name
        </label>
        <input
          id="list-name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          maxLength={60}
          placeholder="e.g. GlowRite Q4 · monthly payout"
          className="mt-1.5 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
        />
      </Modal>
    </div>
  );
}